import { actionTitle } from '../ReportMetrics/detail';
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ActionPoints.module.scss';
import { inAppClick } from '../../utils/linkClick';
import {
  IconPlus,
  IconListNumbers,
  IconLayoutBoard
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';

import { ProjectDto, Status, ActionPointDto, PermissionKey} from '../../api/model';
import { useGetActionpointProjectIdProject, usePutActionpointId, getGetActionpointProjectIdProjectQueryKey } from '../../api/action-point-api/action-point-api';
import { useNotifications } from '../../hooks/useNotifications';
import { useTenantUsers } from '../../hooks/useTenantUsers';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import CreateActionPointModal from './CreateActionPointModal';
import EditActionPointModal from './EditActionPointModal';

interface ActionPointsProps {
  project?: ProjectDto;
  onDataRefresh?: () => Promise<any>;
}

interface ActionPointItem {
  id: string;
  actionPointId: number;
  name: string;
  comment: string;
  validationCriteria?: string;
  validationMethod?: string;
  assignedPerson?: string;
  status: 'ready' | 'inProgress' | 'inReview' | 'done';
  created?: string;
  modified?: string;
  checkPointId?: number;
  validationDate?: string;
  actualValidationDate?: string;
}

const ActionPoints: React.FC<ActionPointsProps> = ({
  project,
  onDataRefresh,
}) => {
  const { t } = useTranslation(['actionPoints', 'templates']);
  const { data: allActionPoints, isLoading: isLoadingActionPoints } = useGetActionpointProjectIdProject(
    project?.id || 0,
    {
      query: {
        enabled: !!project?.id,
        refetchOnMount: true,
      },
    }
  );
  const queryClient = useQueryClient();

  // Load saved view preference
  const [currentView, setCurrentView] = useState<'list' | 'board'>(() => {
    const savedView = localStorage.getItem('actionPointsView');
    return (savedView === 'list' || savedView === 'board') ? savedView : 'list';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('actionPointsItemsPerPage');
    return saved ? parseInt(saved, 10) || 8 : 8;
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedActionPointForEdit, setSelectedActionPointForEdit] = useState<ActionPointDto | null>(null);
  const [draggedActionPoint, setDraggedActionPoint] = useState<ActionPointItem | null>(null);

  // Hooks
  const updateActionPointMutation = usePutActionpointId();
  const { notifySuccess, notifyError } = useNotifications();
  const { namesByIds: assigneeNames } = useTenantUsers();
  const { can } = useCurrentUserPermissions();
  const canCreateActionPoint = can(PermissionKey.CREATE_ACTION_ITEMS, 'CREATE');

  // Save view preference
  useEffect(() => {
    localStorage.setItem('actionPointsView', currentView);
  }, [currentView]);

  // Status mapping for board view
  const mapStatusToBoardStatus = (status?: Status): 'ready' | 'inProgress' | 'inReview' | 'done' => {
    switch (status) {
      case Status.PLANNED:
        return 'ready';
      case Status.IN_PROGRESS:
        return 'inProgress';
      case Status.DELAYED:
        return 'inReview';
      case Status.FINISHED:
        return 'done';
      case Status.CANCELLED:
        return 'ready';
      default:
        return 'ready';
    }
  };

  // Get display label for board status
  const getStatusDisplayLabel = (status: 'ready' | 'inProgress' | 'inReview' | 'done'): string => {
    const labels = {
      'ready': t('status.ready'),
      'inProgress': t('status.inProgress'),
      'inReview': t('status.inReview'),
      'done': t('status.done')
    };
    return labels[status];
  };

  // Convert action point data
  const processedActionPointData = useMemo((): ActionPointItem[] => {
    if (!allActionPoints) return [];

    return allActionPoints.map((actionPoint) => {
      return {
        id: `AP${actionPoint.id || 0}`,
        actionPointId: actionPoint.id || 0,
        // rule-raised ones are stored with the rule's English label; show its translation
        name: actionPoint.ruleKey ? actionTitle(t, actionPoint) : actionPoint.name || t('loading.unnamed'),
        comment: actionPoint.comment || t('loading.noComment'),
        validationCriteria: actionPoint.validationCriteria,
        validationMethod: actionPoint.validationMethod,
        assignedPerson: actionPoint.assignedUserIds?.length ? assigneeNames(actionPoint.assignedUserIds) : undefined,
        status: mapStatusToBoardStatus(actionPoint.status),
        created: actionPoint.created?.toString(),
        modified: actionPoint.modified?.toString(),
        checkPointId: actionPoint.checkPointId,
        validationDate: actionPoint.validationDate?.toString(),
        actualValidationDate: actionPoint.actualValidationDate?.toString(),
      };
    });
  }, [allActionPoints, assigneeNames]);

  const actionPointData = processedActionPointData;

  // find total pages
  const totalPages = Math.ceil(actionPointData.length / itemsPerPage);

  const currentItems = actionPointData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Pagination
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    localStorage.setItem('actionPointsItemsPerPage', newItemsPerPage.toString());
    setCurrentPage(1);
  };

  const handleActionPointCreated = async () => {
    setIsCreateModalOpen(false);

    if (project?.id) {
      await queryClient.invalidateQueries({ 
        queryKey: getGetActionpointProjectIdProjectQueryKey(project.id) 
      });
    }

    // refresh on update
    if (onDataRefresh) {
      try {
        await onDataRefresh();
      } catch (error) {
        console.error('Failed to refresh project data:', error);
      }
    }
  };

  const handleEditActionPoint = (actionPoint: ActionPointItem) => {
    // Find the action point details
    const originalActionPoint = allActionPoints?.find(ap => ap.id === actionPoint.actionPointId);
    if (originalActionPoint) {
      setSelectedActionPointForEdit(originalActionPoint);
    }
  };

  const handleActionPointUpdated = async () => {
    setSelectedActionPointForEdit(null);

    if (project?.id) {
      await queryClient.invalidateQueries({ 
        queryKey: getGetActionpointProjectIdProjectQueryKey(project.id) 
      });
    }

    // refresh on update
    if (onDataRefresh) {
      try {
        await onDataRefresh();
      } catch (error) {
        console.error('Failed to refresh project data after edit:', error);
      }
    }
  };

  // Drag drop handlings
  const handleDragStart = (e: React.DragEvent, actionPoint: ActionPointItem) => {
    setDraggedActionPoint(actionPoint);
    e.dataTransfer.effectAllowed = 'move';
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedActionPoint(null);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: 'ready' | 'inProgress' | 'inReview' | 'done') => {
    e.preventDefault();
    
    if (!draggedActionPoint) return;

    // if status no change, do nothing
    if (draggedActionPoint.status === newStatus) {
      return;
    }

    try {
      // map board match with dto status
      const statusMap: Record<'backlog' | 'ready' | 'inProgress' | 'inReview' | 'done', Status> = {
        'backlog': Status.BACKLOG,
        'ready': Status.PLANNED,
        'inProgress': Status.IN_PROGRESS,
        'inReview': Status.DELAYED,
        'done': Status.FINISHED
      };

      const apiStatus = statusMap[newStatus];

      // Find the original action point
      const originalActionPoint = allActionPoints?.find(
        ap => ap.id === draggedActionPoint.actionPointId
      );

      if (!originalActionPoint) {
        notifyError(t('title'), t('loading.notFound'));
        return;
      }

      // Update the action point new status
      const updatedActionPointData: ActionPointDto = {
        id: originalActionPoint.id,
        name: originalActionPoint.name,
        comment: originalActionPoint.comment,
        validationCriteria: originalActionPoint.validationCriteria,
        validationMethod: originalActionPoint.validationMethod,
        assignedUserIds: originalActionPoint.assignedUserIds,
        status: apiStatus,
        projectId: originalActionPoint.projectId,
        created: originalActionPoint.created,
        modified: originalActionPoint.modified
      };

      await updateActionPointMutation.mutateAsync({
        id: draggedActionPoint.actionPointId,
        data: updatedActionPointData
      });

      notifySuccess(t('title'), `"${draggedActionPoint.name}" → ${getStatusDisplayLabel(newStatus)}`);

      if (project?.id) {
        await queryClient.invalidateQueries({ 
          queryKey: getGetActionpointProjectIdProjectQueryKey(project.id) 
        });
      }

      // Refresh data
      if (onDataRefresh) {
        await onDataRefresh();
      }
    } catch (error) {
      console.error('Error updating action point status:', error);
      notifyError(t('title'), error);
    }
  };

  return (
    <>
      <div className={styles.planHeader}>
        <div className={styles.planHeaderContent}>
          <h2 className={styles.planTitle}>{t('title')}</h2>
          <p className={styles.planDescription}>{t('description')}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px', background: '#F5F7FA', padding: '4px', borderRadius: '8px' }}>
            <button
              onClick={() => setCurrentView('list')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                background: currentView === 'list' ? '#fff' : 'transparent',
                color: currentView === 'list' ? '#2D78DB' : '#525866',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
                boxShadow: currentView === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <IconListNumbers size={18} />
              {t('viewToggle.list')}
            </button>
            <button
              onClick={() => setCurrentView('board')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                background: currentView === 'board' ? '#fff' : 'transparent',
                color: currentView === 'board' ? '#2D78DB' : '#525866',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
                boxShadow: currentView === 'board' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <IconLayoutBoard size={18} />
              {t('viewToggle.board')}
            </button>
          </div>
          {canCreateActionPoint && (
            <button
              className={styles.createTaskButton}
              onClick={() => setIsCreateModalOpen(true)}
            >
              <IconPlus size={18} />
              {t('createButton')}
            </button>
          )}
        </div>
      </div>

      <div className={styles.mainContainer}>
        <div className={styles.tableWrapper} style={{ width: '100%' }}>
          {isLoadingActionPoints ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3>{t('loading.title')}</h3>
                <p>{t('loading.description')}</p>
              </div>
            </div>
          ) : actionPointData.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateContent}>
                <h3>{t('emptyState.title')}</h3>
                <p>{t('emptyState.description')}</p>
              </div>
            </div>
          ) : currentView === 'list' ? (
            <div className={styles.table}>
              <div className={styles.content}>
                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.id')}>{t('table.id')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <a
                      key={item.id}
                      href={`/project/${project?.id}?section=task-action-points&actionPointId=${item.actionPointId}`}
                      className={styles.tableCell}
                      onClick={inAppClick(() => handleEditActionPoint(item))}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.text}>{item.id}</span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.name')}>{t('table.name')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <a
                      key={item.id}
                      href={`/project/${project?.id}?section=task-action-points&actionPointId=${item.actionPointId}`}
                      className={styles.tableCell}
                      onClick={inAppClick(() => handleEditActionPoint(item))}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.truncatedText} title={item.name}>
                          {item.name.length > 30 
                            ? `${item.name.substring(0, 30)}...` 
                            : item.name}
                        </span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.comment')}>{t('table.comment')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditActionPoint(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.truncatedText} title={item.comment}>
                          {item.comment.length > 35 
                            ? `${item.comment.substring(0, 35)}...` 
                            : item.comment}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.validationCriteria')}>{t('table.validationCriteria')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditActionPoint(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.text}>{item.validationCriteria || '-'}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.validationMethod')}>{t('table.validationMethod')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditActionPoint(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.text}>{item.validationMethod || '-'}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.assignedPerson')}>{t('table.assignedPerson')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div 
                      key={item.id} 
                      className={styles.tableCell}
                      onClick={() => handleEditActionPoint(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={styles.text}>{item.assignedPerson || '-'}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.status')}>{t('table.status')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      className={styles.tableCell}
                      onClick={() => handleEditActionPoint(item)}
                    >
                      <div className={styles.textContent}>
                        <span className={`${styles.statusBadge} ${styles[item.status]}`}>
                          {getStatusDisplayLabel(item.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={styles.column}>
                  <div className={styles.headerCell}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerText} title={t('table.checkpoint', 'Checkpoint')}>{t('table.checkpoint', 'Checkpoint')}</span>
                    </div>
                  </div>
                  {currentItems.map((item) => (
                    <div
                      key={item.id}
                      className={styles.tableCell}
                      onClick={() => handleEditActionPoint(item)}
                    >
                      <div className={styles.textContent}>
                        {item.checkPointId ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '2px 8px', borderRadius: 5,
                            background: '#EFF6FF', color: '#2563EB',
                            fontSize: 12, fontWeight: 600, border: '1px solid #BFDBFE',
                          }}>
                            CP-{item.checkPointId}
                          </span>
                        ) : (
                          <span className={styles.text}>—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.pagination}>
                <div className={styles.paginationLeft}>
                  <span className={styles.itemsPerPageLabel}>{t('pagination.show')}</span>
                  <select
                    className={styles.itemsPerPageSelect}
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={8}>8</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                  <span className={styles.itemsPerPageLabel}>{t('pagination.perPage')}</span>
                </div>

                <span className={styles.paginationDetails}>
                  {totalPages > 0 ? (
                    <>
                      {t('pagination.pageOf', { page: currentPage, total: totalPages })}
                      {actionPointData.length > 0 && (
                        <span> • {t('pagination.showingRange', { from: ((currentPage - 1) * itemsPerPage) + 1, to: Math.min(currentPage * itemsPerPage, actionPointData.length), total: actionPointData.length })}</span>
                      )}
                    </>
                  ) : (
                    t('emptyState.title')
                  )}
                </span>

                <div className={styles.paginationRight}>
                  <button
                    className={styles.paginationButton}
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || totalPages === 0}
                    aria-label={t('pagination.prevAria')}
                  >
                    {t('pagination.previous')}
                  </button>
                  <button
                    className={styles.paginationButton}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                    aria-label={t('pagination.nextAria')}
                  >
                    {t('pagination.next')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.boardContainer}>
              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'ready')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.ready')}</div>
                </div>
                <div className={styles.columnContent}>
                  {actionPointData.filter(ap => ap.status === 'ready').map(actionPoint => (
                    <div
                      key={actionPoint.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, actionPoint)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditActionPoint(actionPoint)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{actionPoint.name.replaceAll('_', ' ')}</div>
                          </div>
                          {actionPoint.checkPointId && (
                            <div style={{ marginBottom: 4 }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '1px 7px', borderRadius: 5,
                                background: '#EFF6FF', color: '#2563EB',
                                fontSize: 11, fontWeight: 600, border: '1px solid #BFDBFE',
                              }}>
                                CP-{actionPoint.checkPointId}
                              </span>
                            </div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {actionPoint.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{actionPoint.comment}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'inProgress')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.inProgress')}</div>
                </div>
                <div className={styles.columnContent}>
                  {actionPointData.filter(ap => ap.status === 'inProgress').map(actionPoint => (
                    <div
                      key={actionPoint.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, actionPoint)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditActionPoint(actionPoint)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{actionPoint.name.replaceAll('_', ' ')}</div>
                          </div>
                          {actionPoint.checkPointId && (
                            <div style={{ marginBottom: 4 }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '1px 7px', borderRadius: 5,
                                background: '#EFF6FF', color: '#2563EB',
                                fontSize: 11, fontWeight: 600, border: '1px solid #BFDBFE',
                              }}>
                                CP-{actionPoint.checkPointId}
                              </span>
                            </div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {actionPoint.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{actionPoint.comment}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'inReview')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.inReview')}</div>
                </div>
                <div className={styles.columnContent}>
                  {actionPointData.filter(ap => ap.status === 'inReview').map(actionPoint => (
                    <div
                      key={actionPoint.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, actionPoint)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditActionPoint(actionPoint)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{actionPoint.name.replaceAll('_', ' ')}</div>
                          </div>
                          {actionPoint.checkPointId && (
                            <div style={{ marginBottom: 4 }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '1px 7px', borderRadius: 5,
                                background: '#EFF6FF', color: '#2563EB',
                                fontSize: 11, fontWeight: 600, border: '1px solid #BFDBFE',
                              }}>
                                CP-{actionPoint.checkPointId}
                              </span>
                            </div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {actionPoint.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{actionPoint.comment}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div 
                className={styles.boardColumn}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, 'done')}
              >
                <div className={styles.columnHeader}>
                  <div className={styles.columnTitle}>{t('status.done')}</div>
                </div>
                <div className={styles.columnContent}>
                  {actionPointData.filter(ap => ap.status === 'done').map(actionPoint => (
                    <div
                      key={actionPoint.id}
                      className={styles.taskCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, actionPoint)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEditActionPoint(actionPoint)}
                      style={{ cursor: 'grab' }}
                    >
                      <div className={styles.taskCardInner}>
                        <div className={styles.taskContent}>
                          <div className={styles.taskTitle}>
                            <div className={styles.taskTitleText}>{actionPoint.name.replaceAll('_', ' ')}</div>
                          </div>
                          {actionPoint.checkPointId && (
                            <div style={{ marginBottom: 4 }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '1px 7px', borderRadius: 5,
                                background: '#EFF6FF', color: '#2563EB',
                                fontSize: 11, fontWeight: 600, border: '1px solid #BFDBFE',
                              }}>
                                CP-{actionPoint.checkPointId}
                              </span>
                            </div>
                          )}
                          <div className={styles.taskAssignee}>
                            {t('table.assignedLabel')} {actionPoint.assignedPerson || t('table.notAssigned')}
                          </div>
                          <div className={styles.taskSummary}>{actionPoint.comment}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateActionPointModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onActionPointCreated={handleActionPointCreated}
        project={project}
      />

      <EditActionPointModal
        isOpen={!!selectedActionPointForEdit}
        onClose={() => setSelectedActionPointForEdit(null)}
        onActionPointUpdated={handleActionPointUpdated}
        actionPointData={selectedActionPointForEdit || undefined}
      />
    </>
  );
};

export default ActionPoints;
